import {
  buildProjectThemeCssProperties,
  compositionSiteDataAttributes,
  resolveNavGround,
  resolveProjectColorSet,
} from '@web-studio/shared-schemas';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { BlockRenderer } from './BlockRenderer';
import { resolveEditTargetFromElement } from './lib/resolveEditTarget';
import type { SitePageProps } from './types';

export function SitePage({
  blocks,
  locale,
  activeBlockId = null,
  activeEditTarget = null,
  themeId = 'michelin-dark',
  colorsetId,
  spacingEnum = 'balanced',
  cornerEnum,
  typographyEnum,
  editable = false,
  onBlockSelect,
  pages,
  defaultLocale,
  supportedLocales,
  siteMode,
  vertical,
  hasTastingTimeline,
  pathname,
}: SitePageProps): ReactNode {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);
  const resolved = resolveProjectColorSet(themeId, colorsetId, { cornerEnum, typographyEnum });
  const themeStyle: CSSProperties | undefined = buildProjectThemeCssProperties(
    themeId,
    colorsetId,
    spacingEnum,
    { cornerEnum, typographyEnum },
  ) as CSSProperties | undefined;
  const compositionAttrs = compositionSiteDataAttributes(themeId);
  const navGround = resolveNavGround(blocks);

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!editable || !onBlockSelect) {
      return;
    }

    const editTarget = resolveEditTargetFromElement(event.target as HTMLElement);
    if (!editTarget) {
      return;
    }

    const blockEl = (event.target as HTMLElement).closest('[data-block-id]');
    const blockId = blockEl?.getAttribute('data-block-id');
    if (!blockId) {
      return;
    }

    event.preventDefault();
    onBlockSelect(blockId, editTarget);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!editable || !onBlockSelect) {
      return;
    }

    if (resolveEditTargetFromElement(event.target as HTMLElement)) {
      return;
    }

    const blockEl = (event.target as HTMLElement).closest('[data-block-id]');
    const blockId = blockEl?.getAttribute('data-block-id');
    if (!blockId) {
      return;
    }

    onBlockSelect(blockId);
  };

  return (
    <div
      className={['ws-site', editable ? 'ws-site--editable' : ''].filter(Boolean).join(' ')}
      data-theme={themeId}
      data-nav-ground={navGround}
      {...(resolved ? { 'data-colorset': resolved.colorsetId } : {})}
      {...compositionAttrs}
      {...(themeStyle ? { style: themeStyle } : {})}
      id="ws-site-root"
      onClickCapture={editable ? handleClickCapture : undefined}
      onClick={editable ? handleClick : undefined}
      role={editable ? 'presentation' : undefined}
    >
      {ordered.map((block) => (
        <BlockRenderer
          key={block.blockId}
          block={block}
          locale={locale}
          activeBlockId={activeBlockId}
          activeEditTarget={activeEditTarget}
          editable={editable}
          allBlocks={blocks}
          themeId={themeId}
          colorsetId={colorsetId}
          pages={pages}
          defaultLocale={defaultLocale}
          supportedLocales={supportedLocales}
          siteMode={siteMode}
          vertical={vertical}
          hasTastingTimeline={hasTastingTimeline}
          pathname={pathname}
        />
      ))}
    </div>
  );
}
